import { message, Modal } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  deleteApplication,
  getApplicationsByGroup,
  updateApplicationRound,
} from "../../api/application";
import { useSettings } from "../../contexts/settingsContext";
import { loadState } from "../../stores/store";
import ApplicationTable from "../Applications/applicationsTable";

const GroupApplicationsModal = ({ group, visible, onCancel }) => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const { state: settings } = useSettings();

  const fetchApplications = useCallback(async () => {
    if (!group?._id) return;

    try {
      setLoading(true);

      const applicationsData = await getApplicationsByGroup(group._id);
      const filteredApplications = applicationsData.filter(
        (app) => app.round === group.round
      );

      const uniqueApplicationsMap = new Map();
      filteredApplications.forEach((app) => {
        const eventId = app.event._id || app.event;
        if (!uniqueApplicationsMap.has(eventId)) {
          uniqueApplicationsMap.set(eventId, app);
        }
      });

      const uniqueApplications = Array.from(uniqueApplicationsMap.values());
      setApplications(uniqueApplications);
    } catch (error) {
      console.error("Error fetching applications by group:", error);
      message.error("Failed to load applications");
    } finally {
      setLoading(false);
    }
  }, [group]);

  useEffect(() => {
    if (visible) {
      fetchApplications();
    }
  }, [visible, group, settings.selectedRound, fetchApplications]);

  const handleDelete = useCallback(
    async (applicationId) => {
      try {
        await deleteApplication(applicationId);
        message.success("Application deleted successfully");
        fetchApplications();
      } catch (error) {
        message.error("Failed to delete application");
        console.error("Error deleting application:", error);
      }
    },
    [fetchApplications]
  );

  const isAdmin = useMemo(() => loadState().user.role === "admin", []);

  const handleRowClick = useCallback(
    (index) => {
      setSelectedRow(index);
      setSelectedApplication(applications[index]);
    },
    [applications]
  );

  const handleMoveToNextRound = useCallback(
    async (applicationId) => {
      try {
        await updateApplicationRound(applicationId);
        message.success("Application moved to next round");
        fetchApplications();
      } catch (error) {
        message.error("Failed to move application to next round");
        console.error("Error moving application to next round:", error);
      }
    },
    [fetchApplications]
  );

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      title={`Applications for ${group.group_name} - Round ${settings.selectedRound}`}
      width={1000}
      footer={null}
    >
      <ApplicationTable
        loading={loading}
        applications={applications}
        isAdmin={isAdmin}
        onRowClick={handleRowClick}
        onDelete={handleDelete}
        onMoveToNextRound={handleMoveToNextRound}
        onApplicationUpdate={fetchApplications}  // 👈 this is the key
      />
    </Modal>
  );
};

export default GroupApplicationsModal;